// Project Sentinel - Login / Landing Page
import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Zap, TrendingUp, BarChart3, Lightbulb, ArrowRight, ArrowLeft, Building2, Loader2, Eye, EyeOff } from 'lucide-react'
import { cn } from '@/lib/utils'
import { fadeInUp, stagger } from '@/lib/animations'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { api } from '../services/api'
import { prefetchDefaultDashboard } from '../services/dashboard-prefetch'
import { postLoginTarget } from '@/lib/postLoginTarget'
import { isRouted } from '@/services/nomos/switch'
import { heldTenants, switchTenant, TenantChoiceRequired, type TenantOption } from '@/services/nomos/tenant'

interface LoginPageProps {
  onLogin: () => void
}

const features = [
  {
    icon: BarChart3,
    title: 'Analyze',
    description: 'Discover insights and identify optimization opportunities across your portfolio',
  },
  {
    icon: TrendingUp,
    title: 'Track',
    description: 'Monitor energy consumption, weather data, and device performance in real-time',
  },
  {
    icon: Lightbulb,
    title: 'Optimize',
    description: 'Implement actions to reduce energy use and lower your carbon footprint',
  },
]

/** Set by the organisation switcher: the organisation to open once the password is checked, and the email. */
interface SwitchIntent { org?: string; name?: string; email?: string }

export default function LoginPage({ onLogin }: LoginPageProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const intent = (location.state ?? {}) as SwitchIntent
  const [email, setEmail] = useState(intent.email ?? '')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  // TZ-2: email + password only. NomOS finds the person's organisations; when the password matches in
  // several, a picker of their names follows (never slugs). Sessions stay per organisation (B7).
  const nomos = isRouted('login')
  const held = nomos ? heldTenants() : []
  const [choice, setChoice] = useState<{ token: string; tenants: TenantOption[] } | null>(null)
  const [choosing, setChoosing] = useState<string | null>(null)
  // The page can mount just before the switcher's navigate() delivers its state: fill the email when it lands.
  useEffect(() => { if (intent.email) setEmail((e) => e || intent.email || '') }, [intent.email])

  // Deep links survive the login, but never into another organisation's state (the switcher sends /login).
  const target = postLoginTarget(location)

  const resume = (slug: string) => {
    // Full load: nothing of a previously active organisation survives in memory.
    if (switchTenant(slug)) window.location.assign(target)
  }

  const signedIn = () => {
    // the dashboard's read-ahead: its definition and its widgets' questions, under the keys its widgets ask
    void prefetchDefaultDashboard()
    onLogin()
    navigate(target, { replace: true })
  }

  const pick = async (token: string, slug: string) => {
    setError('')
    setChoosing(slug)
    try {
      await api.chooseTenant(token, slug)
      signedIn()
    } catch {
      // single use and short-lived: start again from the password
      setChoice(null)
      setPassword('')
      setError('Your sign-in expired. Please sign in again.')
    } finally {
      setChoosing(null)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      await api.login(email, password)
      signedIn()
    } catch (err) {
      if (err instanceof TenantChoiceRequired) {
        const wanted = intent.org && err.tenants.find((t) => t.slug === intent.org)
        if (wanted) { setIsLoading(false); await pick(err.token, wanted.slug); return }
        setChoice({ token: err.token, tenants: err.tenants })
      } else if ((err as { response?: { status?: number } }).response?.status === 429) {
        setError('Too many attempts. Please wait a few minutes and try again.')
      } else {
        setError('Invalid email or password')
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left side - Hero */}
      {/* Fixed cobalt background + explicit white text — theme-stable in both light and dark */}
      <div className="hidden lg:flex lg:w-3/5 bg-blue-900 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-[0.03]">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>
              <pattern id="grid" width="8" height="8" patternUnits="userSpaceOnUse">
                <path d="M 8 0 L 0 0 0 8" fill="none" stroke="currentColor" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100" height="100" fill="url(#grid)" className="text-white" />
          </svg>
        </div>

        {/* Decorative blurs */}
        <div className="absolute -top-48 -left-48 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-48 -right-48 w-[500px] h-[500px] bg-white/5 rounded-full blur-3xl" />

        {/* Content */}
        <motion.div
          className="relative z-10 flex flex-col justify-center px-16 xl:px-24 text-white"
          initial="hidden"
          animate="visible"
          variants={stagger}
        >
          {/* Logo */}
          <motion.div variants={fadeInUp} className="mb-12">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/20">
                <Zap className="w-7 h-7 text-white" />
              </div>
              <span className="text-2xl font-semibold tracking-tight text-white">Sentinel</span>
            </div>
          </motion.div>

          {/* Headline */}
          <motion.h1 variants={fadeInUp} className="text-4xl xl:text-5xl font-bold leading-tight mb-6 text-white">
            Intelligent Energy
            <br />
            Management
          </motion.h1>

          <motion.p variants={fadeInUp} className="text-lg xl:text-xl text-white/80 mb-12 max-w-lg leading-relaxed">
            Transform your building portfolio with data-driven insights. Reduce costs, lower emissions, and optimize energy usage.
          </motion.p>

          {/* Feature cards */}
          <motion.div variants={stagger} className="space-y-4">
            {features.map((feature) => (
              <motion.div
                key={feature.title}
                variants={fadeInUp}
                className={cn(
                  'flex items-start gap-4 p-4 rounded-xl transition-colors',
                  'bg-white/5 border border-white/10',
                  'hover:bg-white/10'
                )}
              >
                <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm mb-1 text-white">{feature.title}</h3>
                  <p className="text-sm text-white/70 leading-relaxed">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>

      {/* Right side - Login form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div
          className="w-full max-w-sm"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center mx-auto mb-4">
              <Zap className="w-6 h-6 text-primary-foreground" />
            </div>
            <h1 className="text-xl font-semibold text-foreground">Sentinel</h1>
            <p className="text-sm text-muted-foreground mt-1">Energy Management Platform</p>
          </div>

          {/* Login card */}
          <Card className="border-border/50 shadow-lg">
            {choice ? (
              <>
                <CardHeader className="space-y-1 pb-4">
                  <CardTitle className="text-2xl">Choose an organisation</CardTitle>
                  <CardDescription>Your account belongs to more than one organisation.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2" data-testid="tenant-picker">
                    {choice.tenants.map((t) => (
                      <Button
                        key={t.slug}
                        type="button"
                        variant="outline"
                        className="w-full justify-between h-11"
                        disabled={choosing !== null}
                        onClick={() => pick(choice.token, t.slug)}
                      >
                        <span className="flex items-center gap-2 truncate">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                          <span className="truncate">{t.name}</span>
                        </span>
                        {choosing === t.slug ? <Loader2 className="ml-2 h-4 w-4 animate-spin" /> : <ArrowRight className="ml-2 h-4 w-4" />}
                      </Button>
                    ))}
                  </div>
                  <Button
                    type="button"
                    variant="link"
                    className="px-0 mt-4 h-auto text-xs text-muted-foreground"
                    disabled={choosing !== null}
                    onClick={() => { setChoice(null); setPassword('') }}
                  >
                    <ArrowLeft className="mr-1 h-3 w-3" />
                    Use a different account
                  </Button>
                </CardContent>
              </>
            ) : (
            <>
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl">Welcome back</CardTitle>
              <CardDescription>{intent.org && intent.name ? `Enter your password to open ${intent.name}` : 'Sign in to your account to continue'}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="flex items-center gap-2 bg-destructive/10 text-destructive px-3 py-2.5 rounded-lg text-sm border border-destructive/20"
                  >
                    <span className="text-xs">{error}</span>
                  </motion.div>
                )}

                {nomos && held.length > 0 && (
                  <div className="space-y-2" data-testid="held-tenants">
                    <p className="text-xs text-muted-foreground">Already signed in</p>
                    {held.map((t) => (
                      <Button key={t.slug} type="button" variant="outline" className="w-full justify-between" onClick={() => resume(t.slug)}>
                        <span className="truncate">{t.name}</span>
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    ))}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    <Button
                      type="button"
                      variant="link"
                      className="px-0 h-auto text-xs text-muted-foreground"
                      onClick={() => navigate('/forgot-password')}
                    >
                      Forgot password?
                    </Button>
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    <>
                      Sign in
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>

              </form>
            </CardContent>
            </>
            )}
          </Card>

          {/* Footer */}
          <p className="text-center text-xs text-muted-foreground mt-8">
            &copy; {new Date().getFullYear()} Sentinel. All rights reserved.
          </p>
        </motion.div>
      </div>
    </div>
  )
}
